import Input from '@/components/Common/Input';
import Switch from '@/components/Common/Switch';
import Tooltip from '@/components/Common/Tooltip';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';
import copy from 'copy-to-clipboard';

type CharmHealthFormProps = {
  namePrefix: string;
  control: any;
  disabled?: boolean;
  /** When true (live + active), also show refresh_token alongside authorization code */
  isActive?: boolean;
};

const CharmHealthForm = ({ namePrefix, control, disabled, isActive = false }: CharmHealthFormProps) => {
  const watch = useWatch({ control, name: namePrefix });
  const [copied, setCopied] = useState(false);
  const isLiveMode = Boolean(watch?.live_mode);

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      <Box display='flex' alignItems='center' width='100%' mt={2}>
        <Typography variant='h6' fontSize={15} fontWeight={500}>
          Test Mode
        </Typography>
        <Switch name={`${namePrefix}.live_mode`} control={control} disabled={disabled} />
        <Typography variant='h6' fontSize={15} fontWeight={500}>
          Live Mode
        </Typography>
      </Box>

      {isLiveMode ? (
        <>
          <Box p={2} mb={2} borderRadius={2} bgcolor='grey.100' border='1px solid' borderColor='grey.300'>
            <Typography variant='subtitle2' fontWeight={600} mb={1}>
              How to Generate Your Authorization Code
            </Typography>

            <Typography variant='body2' color='textSecondary' mb={1}>
              To generate the authorization code, please open the following Redirect URI in your browser and complete
              the authorization process:
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Click to copy the URL'} placement='top'>
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  copy(
                    `https://accounts.charmtracker.com/oauth/v2/auth?client_id=${watch?.client_id}&state=test&response_type=code&redirect_uri=https://ehr2.charmtracker.com/ehr/physician/mySpace.do?ACTION=SHOW_OAUTH_JSON&access_type=offline &prompt=consent&scope=charmhealth.user.setting.facility.READ,charmhealth.user.setting.member.READ,charmhealth.patient.demographics.ALL,charmhealth.user.calendar.ALL`
                  );
                  setCopied(true);
                  setTimeout(() => {
                    setCopied(false);
                  }, 1500);
                }}
              >
                <Typography variant='body2' color='primary' sx={{ wordBreak: 'break-all' }}>
                  https://accounts.charmtracker.com/oauth/v2/auth?client_id={watch?.client_id}
                  &state=test&response_type=code&redirect_uri=https://ehr2.charmtracker.com/ehr/physician/mySpace.do?ACTION=SHOW_OAUTH_JSON&access_type=offline
                  &prompt=consent&scope=charmhealth.user.setting.facility.READ,charmhealth.user.setting.member.READ,charmhealth.patient.demographics.ALL,charmhealth.user.calendar.ALL
                </Typography>
              </Box>
            </Tooltip>
          </Box>

          <Input
            name={`${namePrefix}.authorize_code`}
            label='Authorization Code'
            control={control}
            disabled={disabled}
            placeholder='Enter Authorization Code'
            required={!isActive}
            fullWidth
          />

          {/* Live + active: show stored refresh token; inactive/reactivate: auth code only */}
          {isActive && (
            <Input
              name={`${namePrefix}.refresh_token`}
              label='Refresh Token'
              control={control}
              disabled={disabled}
              placeholder='Enter Refresh Token'
              required
              fullWidth
            />
          )}
        </>
      ) : (
        <Input
          name={`${namePrefix}.refresh_token`}
          label='Refresh Token'
          control={control}
          disabled={disabled}
          placeholder='Enter Refresh Token'
          required
          fullWidth
        />
      )}
    </Box>
  );
};

export default CharmHealthForm;
